// lib/copilotOauth.ts — GitHub Copilot OAuth device flow driver (spec §4.4).
// Pure + fully injectable (start/poll/open/sleep) so it's unit-testable. The access
// token is exchanged and stored SERVER-SIDE; the browser only sees the user code.
import type { CopilotOauthStart } from './api';

export interface DeviceFlowDeps {
  start: () => Promise<CopilotOauthStart>;
  poll: (deviceCode: string) => Promise<{ status: 'pending' | 'ok'; error?: string }>;
  open: (url: string) => void;
  sleep: (ms: number) => Promise<void>;
  now?: () => number;
  onCode?: (userCode: string, verificationUri: string) => void;
}

/** Begin the device flow, open the browser, and poll until authorized (or time out). */
export async function runCopilotDeviceFlow(deps: DeviceFlowDeps): Promise<void> {
  const start = await deps.start();
  deps.open(start.verificationUri);
  deps.onCode?.(start.userCode, start.verificationUri);

  const now = deps.now ?? (() => Date.now());
  const deadline = now() + start.expiresIn * 1000;
  const intervalMs = Math.max(1, start.interval) * 1000;

  while (now() < deadline) {
    await deps.sleep(intervalMs);
    const res = await deps.poll(start.deviceCode);
    if (res.status === 'ok') return;
  }
  throw new Error('Authorization timed out — please try again.');
}
